import { useCallback, useEffect, useState } from "react";
import {
  LayoutDashboard,
  Grid2X2,
  ArrowUpRight,
  Plus,
  Search,
  ChevronDown,
  LogOut,
  Check,
  X,
  Layers3,
  Sparkles,
  CircleHelp,
  SlidersHorizontal,
  Camera,
} from "lucide-react";
import { useAuth } from "../config/AuthContext";
import { creativeApi } from "../config/api";
import CreativeGrid from "../components/CreativeGrid";
import CreativeModal from "../components/CreativeModal";
export default function PlannerPage() {
  const { user, logout } = useAuth();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [layout, setLayout] = useState("calendar");
  const [filter, setFilter] = useState("All creatives");
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [toast, setToast] = useState("");
  const [help, setHelp] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await creativeApi.list();
      setRecords(data.creatives);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(""), 4000);
    return () => clearTimeout(timer);
  }, [toast]);
  const populated = records.filter((r) => r.creativeUrl);
  const approved = populated.filter((r) => r.status === "Approved").length;
  const pending = populated.filter(
    (r) => r.status === "Revision Pending",
  ).length;
  const rejected = populated.filter((r) => r.status === "Rejected").length;
  const filtered = records.filter(
    (r) =>
      (filter === "All creatives" || (r.creativeUrl && r.status === filter)) &&
      (!search.trim() ||
        `${r.caption} ${r.seoKeywords.join(" ")} ${new Date(r.date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", timeZone: "UTC" })}`
          .toLowerCase()
          .includes(search.toLowerCase().trim())),
  );
  const selected = records.find((r) => r._id === selectedId);
  function addCreative() {
    const empty = records.find((r) => !r.creativeUrl);
    if (empty) setSelectedId(empty._id);
    else
      setToast(
        "Every slot has a creative. Select a date to edit or replace it.",
      );
  }
  return (
    <div className="app-shell">
      <aside className="sidebar">
        <a href="/" className="brand">
          forma<span>®</span>
        </a>
        <div className="workspace">
          <span className="workspace-icon">S</span>
          <div>
            <strong>Studio workspace</strong>
            <small>Your ideas live here</small>
          </div>
        </div>
        <span className="nav-label">WORKSPACE</span>
        <nav>
          <button
            className="nav-item active"
            onClick={() => {
              setLayout("calendar");
              setFilter("All creatives");
              setSearch("");
            }}
          >
            <LayoutDashboard size={18} />
            Content planner
            <span className="nav-dot" />
          </button>
          <button
            className={`nav-item ${layout === "instagram" ? "sub-active" : ""}`}
            onClick={() => setLayout("instagram")}
          >
            <Camera size={18} />
            Feed preview
            <ArrowUpRight size={15} />
          </button>
        </nav>
        <div className="sidebar-bottom">
          <div className="inspiration-card">
            <Sparkles size={23} strokeWidth={1.4} />
            <h3>
              A little space.
              <br />A lot of possibility.
            </h3>
            <p>
              Your next great idea
              <br />
              starts right here.
            </p>
            <button onClick={addCreative} disabled={loading || !!error}>
              Make something <ArrowUpRight size={16} />
            </button>
          </div>
          <button className="nav-item" onClick={() => setHelp(!help)}>
            <CircleHelp size={18} />A little guidance
          </button>
          <div className="user-row">
            <span className="avatar">
              {user.name.slice(0, 2).toUpperCase()}
            </span>
            <div>
              <strong>{user.name}</strong>
              <small>Personal workspace</small>
            </div>
            <button
              className="icon-button"
              title="Sign out"
              aria-label="Sign out"
              onClick={logout}
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </aside>
      <main className="main">
        <header className="topbar">
          <div className="breadcrumbs">
            Workspace<span>/</span>
            <strong>Content planner</strong>
          </div>
          <div className="topbar-right">
            <span className="workspace-live">
              <span className="live-dot" /> Your creative workspace
            </span>
            <span className="avatar small">
              {user.name.slice(0, 2).toUpperCase()}
            </span>
            <button
              className="mobile-logout icon-button"
              aria-label="Sign out"
              onClick={logout}
            >
              <LogOut size={16} />
            </button>
          </div>
        </header>
        <div className="page-content">
          <section className="page-heading">
            <div>
              <div className="eyebrow greeting">
                <span /> A HOME FOR YOUR NEXT BIG IDEA
              </div>
              <h1>
                Content planner<span>.</span>
              </h1>
              <p>
                A little planning. A lot of possibility. Let’s make something
                good.
              </p>
            </div>
            <div className="header-actions">
              <label className="layout-control">
                <Grid2X2 size={17} />
                <span>Layout</span>
                <select
                  aria-label="Layout"
                  value={layout}
                  onChange={(e) => setLayout(e.target.value)}
                >
                  <option value="calendar">Calendar</option>
                  <option value="instagram">Instagram Grid</option>
                </select>
                <ChevronDown size={15} />
              </label>
              <button
                className="button primary"
                onClick={addCreative}
                disabled={loading || !!error}
              >
                <Plus size={17} />
                Add creative
              </button>
            </div>
          </section>
          <section className="stats" aria-label="Content overview">
            <div className="stat">
              <span className="stat-icon neutral">
                <Layers3 size={19} />
              </span>
              <div>
                <span>Total creatives</span>
                <strong>
                  {populated.length}
                  <small>of {records.length || 30} content slots</small>
                </strong>
              </div>
              <span className="stat-decoration">↗</span>
            </div>
            <div className="stat">
              <span className="stat-icon green">
                <Check size={19} />
              </span>
              <div>
                <span>Approved</span>
                <strong>
                  {approved}
                  <small>ready to share</small>
                </strong>
              </div>
            </div>
            <div className="stat">
              <span className="stat-icon amber">
                <span className="pending-icon">◷</span>
              </span>
              <div>
                <span>Revision pending</span>
                <strong>
                  {pending}
                  <small>a little fine-tuning</small>
                </strong>
              </div>
            </div>
            <div className="stat">
              <span className="stat-icon pink">
                <X size={18} />
              </span>
              <div>
                <span>Rejected</span>
                <strong>
                  {rejected}
                  <small>room to reimagine</small>
                </strong>
              </div>
            </div>
          </section>
          <section className="board">
            <div className="board-toolbar">
              <div className="filter-tabs">
                {[
                  "All creatives",
                  "Approved",
                  "Revision Pending",
                  "Rejected",
                ].map((tab) => (
                  <button
                    key={tab}
                    className={filter === tab ? "selected" : ""}
                    onClick={() => setFilter(tab)}
                  >
                    {tab}
                    {tab === "All creatives" && <span>{records.length}</span>}
                  </button>
                ))}
              </div>
              <label className="search-box">
                <Search size={16} />
                <input
                  aria-label="Search creatives"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search creatives…"
                />
                {search && (
                  <button
                    aria-label="Clear search"
                    onClick={() => setSearch("")}
                  >
                    <X size={14} />
                  </button>
                )}
              </label>
            </div>
            <div className="board-caption">
              <span>
                {layout === "calendar" ? (
                  <LayoutDashboard size={14} />
                ) : (
                  <Camera size={14} />
                )}
                {layout === "calendar"
                  ? "Your creative canvas"
                  : "Your feed, at a glance"}
                <span className="caption-divider">/</span>
                {filtered.length} content slots
              </span>
              <span>
                <SlidersHorizontal size={13} />
                Newest first
              </span>
            </div>
            {loading ? (
              <div className="calendar-grid" aria-label="Loading creatives">
                {Array.from({ length: 12 }, (_, i) => (
                  <div className="skeleton" key={i} />
                ))}
              </div>
            ) : error ? (
              <div className="empty-state">
                <h2>Let’s reconnect.</h2>
                <p role="alert">{error}</p>
                <button className="button primary" onClick={load}>
                  Try again
                </button>
              </div>
            ) : filtered.length ? (
              <CreativeGrid
                records={filtered}
                layout={layout}
                onSelect={(record) => setSelectedId(record._id)}
                user={user}
              />
            ) : (
              <div className="empty-state">
                <Search size={30} />
                <h2>No creatives found.</h2>
                <p>
                  Try a different keyword or give your filters a fresh start.
                </p>
                <button
                  className="button secondary"
                  onClick={() => {
                    setSearch("");
                    setFilter("All creatives");
                  }}
                >
                  Clear filters
                </button>
              </div>
            )}
          </section>
          <footer className="page-footer">
            <span>
              <span className="live-dot" /> A little more organized. A lot more
              inspired.
            </span>
            <span>
              Made for your creative flow{" "}
              <span className="footer-spark">✳</span>
            </span>
          </footer>
        </div>
      </main>
      {help && (
        <div className="help-panel">
          <button
            className="icon-button"
            aria-label="Close guidance"
            onClick={() => setHelp(false)}
          >
            <X size={18} />
          </button>
          <span className="eyebrow">MAKE YOURSELF AT HOME</span>
          <h3>Your ideas, from plan to post.</h3>
          <p>
            Select any date to add or review a creative. Switch layouts to
            preview your feed. Approve a post to reveal its upload control.
          </p>
          <p>
            Changes are saved to your workspace and stay in sync across both
            views.
          </p>
        </div>
      )}
      {selected && (
        <CreativeModal
          key={selected._id}
          record={selected}
          onClose={() => setSelectedId(null)}
          onUpdate={(creative) =>
            setRecords((current) =>
              current.map((r) => (r._id === creative._id ? creative : r)),
            )
          }
          notify={setToast}
        />
      )}
      {toast && (
        <div className="toast" role="status">
          <Check size={17} />
          {toast}
          <button
            className="icon-button"
            aria-label="Dismiss notification"
            onClick={() => setToast("")}
          >
            <X size={15} />
          </button>
        </div>
      )}
    </div>
  );
}
